import React, { useContext, useMemo, useState } from 'react';
import { groupBy } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { useCompany } from 'src/app/Company.store';
import { normalizeCollections } from 'src/api/helpers/suppressedTickets';
import { UI } from './ui';
import { PropertyContext } from '../../../../../context/property-context';
import { GetCollections } from './api';
import { getCurrentMonthRange } from '../../../../../utils';

import { useGetToken } from '../../../../../api/config';

export const ApiWrapper = () => {
  const { id: propertyId } = useContext(PropertyContext);
  const { hostname } = useCompany();
  const token = useGetToken();

  const [dates, setDates] = useState<[Dayjs, Dayjs]>(getCurrentMonthRange());

  const fromDate = dayjs(dates[0]).format('YYYY-MM-DD');
  const toDate = dayjs(dates[1]).format('YYYY-MM-DD');
  // First 7 days of the visible range — much smaller payload for properties
  // with many containers, so the calendar can render an "upcoming week"
  // preview while the full-month fetch (often 30-50s on large properties)
  // is still in flight.
  const weekEnd = dayjs(dates[0]).add(7, 'day');
  const weekToDate = (
    weekEnd.isAfter(dates[1]) ? dayjs(dates[1]) : weekEnd
  ).format('YYYY-MM-DD');

  const baseOptions = {
    // Cap retries: the collection_log endpoint can take ~50s per attempt on
    // large properties, so the default 3 retries traps the user for ~150s.
    retry: 1,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  } as const;

  const weekQuery = useQuery({
    ...baseOptions,
    queryKey: [
      'get-property-collections-window',
      propertyId,
      fromDate,
      weekToDate,
    ],
    queryFn: () =>
      GetCollections({
        token,
        propertyId,
        fromDate,
        toDate: weekToDate,
      }),
  });

  const monthQuery = useQuery({
    ...baseOptions,
    queryKey: ['get-property-collections', propertyId, fromDate, toDate],
    queryFn: () =>
      GetCollections({
        token,
        propertyId,
        fromDate,
        toDate,
      }),
  });

  // Prefer the full-month data once it arrives; fall back to the week-window
  // preview until then.
  const monthCollections = (monthQuery.data as $TSFixMe)?.data;
  const weekCollections = (weekQuery.data as $TSFixMe)?.data;
  const collections = useMemo(
    () => normalizeCollections(monthCollections ?? weekCollections, hostname),
    [monthCollections, weekCollections, hostname]
  );

  const collectionsGroupedByDate = groupBy(collections, 'date');

  // Only show loading when we have nothing at all to render; an error only
  // counts when both queries failed and we have no cached data to show.
  const loading = !collections && (weekQuery.isLoading || monthQuery.isLoading);
  const hasError = !collections && weekQuery.isError && monthQuery.isError;

  const onRetry = () => {
    weekQuery.refetch();
    monthQuery.refetch();
  };

  return (
    <UI
      rangePickerProps={{ initialRange: dates, onChange: setDates }}
      collections={collectionsGroupedByDate}
      loading={loading}
      hasError={hasError}
      onRetry={onRetry}
    />
  );
};
